import { focusElement } from 'platform/utilities/ui';
import { waitForShadowRoot } from 'platform/utilities/ui/webComponents';
import { getFocusableElements } from 'platform/forms-system/src/js/utilities/ui';

// The class waited a beat before focusing so the web components inside the
// form have rendered.
export const FOCUS_DELAY = 100;

export const ERROR_CLOSE_BUTTON_SELECTOR =
  'button[aria-label="Close notification"]';

export const CANCEL_BUTTON_SELECTOR =
  'va-button[data-testid="cancel-edit-button"]';

// The "copy mailing address" checkbox renders before the SchemaForm, so a
// native input is checked for first.
const NATIVE_INPUT_SELECTOR = 'input:not([type="hidden"]), textarea, select';

const FORM_INPUT_WEB_COMPONENTS = [
  'va-text-input',
  'va-textarea',
  'va-select',
  'va-checkbox',
  'va-radio',
  'va-memorable-date',
  'va-number-input',
  'va-combo-box',
].join(', ');

const focusFirstFocusableElement = editForm => {
  const focusableElement = getFocusableElements(editForm)?.[0];

  if (focusableElement) {
    focusElement(focusableElement);
  }
};

/**
 * Focus the country picker inside va-telephone-input, which lives two shadow
 * roots deep and therefore has to be waited for.
 */
const focusTelephoneInput = (editForm, vaTel) =>
  waitForShadowRoot(vaTel)
    .then(() => {
      const comboBox = vaTel.shadowRoot?.querySelector('va-combo-box');
      return comboBox ? waitForShadowRoot(comboBox) : null;
    })
    .then(() => focusFirstFocusableElement(editForm));

/**
 * Move focus to the first thing the Veteran can type into. Returns a promise
 * only for the telephone input, which is the one case that has to wait on a
 * shadow root; every other path focuses synchronously.
 */
export const focusFirstFormElement = editForm => {
  if (!editForm) {
    return undefined;
  }

  const vaTel = editForm.querySelector?.('va-telephone-input');

  if (vaTel) {
    return focusTelephoneInput(editForm, vaTel);
  }

  const nativeInput = editForm.querySelector?.(NATIVE_INPUT_SELECTOR);

  if (nativeInput) {
    focusElement(nativeInput);
    return undefined;
  }

  // No native input before the web components, so focus the native input
  // inside the first form input component's shadow DOM.
  const firstFormInput = editForm.querySelector?.(FORM_INPUT_WEB_COMPONENTS);

  if (firstFormInput) {
    focusElement('input, textarea, select', {}, firstFormInput);
    return undefined;
  }

  focusFirstFocusableElement(editForm);
  return undefined;
};

/**
 * Focus the native button inside the cancel va-button. Used when the confirm
 * cancel modal closes and the Veteran returns to the edit view.
 */
export const focusCancelButton = editForm => {
  const cancelButton = editForm?.querySelector?.(CANCEL_BUTTON_SELECTOR);
  const shadowButton = cancelButton?.shadowRoot?.querySelector('button');

  if (shadowButton) {
    shadowButton.focus();
  }
};

export const focusErrorCloseButton = () =>
  focusElement(ERROR_CLOSE_BUTTON_SELECTOR);
